import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, displayName: string, country?: string) => Promise<{ error: string | null }>;
  signInWithGoogle: (redirectPath?: string) => Promise<{ error: string | null }>;
  signInWithApple: (redirectPath?: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const syncMonitor = (u: User | null) => {
      import("@/lib/monitoring")
        .then(({ setUserContext }) =>
          setUserContext(u ? { id: u.id, email: u.email ?? undefined } : null),
        )
        .catch(() => {});
    };
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      syncMonitor(s?.user ?? null);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      syncMonitor(data.session?.user ?? null);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);


  const value: AuthCtx = {
    user,
    session,
    loading,
    signIn: async (email, password) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (!error) {
        const { Events } = await import("@/lib/analytics");
        Events.login("email");
      }
      return { error: error?.message ?? null };
    },
    signUp: async (email, password, displayName, country) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/app`,
          data: {
            display_name: displayName,
            ...(country ? { country } : {}),
          },
        },
      });
      if (!error) {
        const { Events } = await import("@/lib/analytics");
        Events.signup("email");
      }
      return { error: error?.message ?? null };
    },
    signInWithGoogle: async (redirectPath = "/app") => {
      const res = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}${redirectPath}`,
      });
      if (res.error) return { error: res.error instanceof Error ? res.error.message : String(res.error) };
      const { Events } = await import("@/lib/analytics");
      Events.login("google");
      return { error: null };
    },
    signInWithApple: async (redirectPath = "/app") => {
      const res = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: `${window.location.origin}${redirectPath}`,
      });
      if (res.error) return { error: res.error instanceof Error ? res.error.message : String(res.error) };
      const { Events } = await import("@/lib/analytics");
      Events.login("apple" as any);
      return { error: null };
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}
